import React, { useCallback, useEffect, useMemo, useState } from "react";
import MainContainer from "../../components/MainContainer";
import CollectionFilter from "../../components/CollectionFilter/CollectionFilter";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteCollection,
  getCollections,
  rateModel,
} from "../../store/slices/model/asyncThunks";
import { useNavigate, useSearchParams } from "react-router-dom";
import debounce from "lodash.debounce";
import {
  selectCollections,
  selectDeleteCollectionPending,
} from "../../store/slices/model/slice";
import Pagination from "../../components/Pagination/Pagination";
import {
  Button,
  Container,
  Content,
  ContentContainer,
  EmptyState,
  PaginationWrapper,
} from "./CollectionPage.styles";
import CollectionModal from "../../components/CollectionModal/CollectionModal";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import CollectionSkeletonLoader from "../../components/utils/CollectionsSkeletonLoader";
import CollectionItem from "../../components/utils/CollectionItem/CollectionItem";
import { setSignUpModalOpen } from "../../store/slices/common/slice";
import GenerateCollectionModal from "../../components/GenerateCollectionModal/GenerateCollectionModal";
import RateCollectionModal from "../../components/RateCollectionModal/RateCollectionModal";
import { toast } from "sonner";

const CollectionPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;
  const search = searchParams.get("search") || "";
  const [searchValue, setSearchValue] = useState(() => search ?? "");
  const loggedIn = useSelector(selectLoggedIn);
  const deleteCollectionPending = useSelector(selectDeleteCollectionPending);
  const { data, loading, total } = useSelector(selectCollections);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [openCollectionModal, setOpenCollectionModal] = useState(false);
  const [selectedCollectionVideo, setSelectedCollectionVideo] = useState(null);
  const [isGenerateCollectionModalOpen, setIsGenerateCollectionModalOpen] =
    useState(false);

  const [showRateCollectionModal, setShowRateCollectionModal] = useState(false);

  const changeHandler = useCallback(
    (text) => {
      searchParams.set("search", text);
      searchParams.set("page", 1);
      setSearchParams(searchParams);
    },
    [setSearchParams, searchParams]
  );

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 300),
    [changeHandler]
  );

  const setPage = (page) => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });

    setTimeout(() => {
      searchParams.set("page", page);
      setSearchParams(searchParams);
    }, 300);
  };

  useEffect(() => {
    if (loggedIn) {
      dispatch(
        getCollections({
          page,
          search,
        })
      );
    }
  }, [dispatch, page, search, loggedIn]);

  const handleDeleteConfirm = () => {
    if (selectedCollectionVideo) {
      dispatch(deleteCollection(selectedCollectionVideo._id))
        .unwrap()
        .then(() => {
          setShowDeleteConfirm(false);
          setOpenCollectionModal(false);
          setSelectedCollectionVideo(null);
        })
        .catch((err) => {
          console.log(err);
          setShowDeleteConfirm(false);
          toast.error(err.message);
        });
    }
  };

  const handleRateCollection = (rating) => {
    if (selectedCollectionVideo) {
      setSelectedCollectionVideo((prev) => {
        return { ...prev, rating };
      });

      dispatch(rateModel({ collectionId: selectedCollectionVideo._id, rating }))
        .unwrap()
        .then(() => {
          //   setShowRateCollectionModal(false);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  const handleCollectionModalClose = () => {
    setOpenCollectionModal(false);
    setSelectedCollectionVideo(null);
  };

  if (loggedIn === null) {
    return null;
  }

  const handleCollectionItemClick = (video) => {
    setSelectedCollectionVideo(video);
    setOpenCollectionModal(true);
  };

  return (
    <MainContainer offPadding hideTabs={false}>
      <Container>
        <CollectionFilter
          searchPlaceholderText="search for title"
          hideFilter
          alwaysExpanded
          hideSearch={!loggedIn || (data !== null && !data?.length && !search)}
          searchValue={searchValue}
          onSearchChange={(value) => {
            setSearchValue(value);
            debouncedChangeHandler(value);
          }}
        />
        <ContentContainer>
          {!loggedIn ? (
            <EmptyState>
              <h3>Login Required</h3>
              <p>You need to be logged in to view your videos.</p>
              <Button onClick={() => dispatch(setSignUpModalOpen(true))}>
                Login
              </Button>
            </EmptyState>
          ) : loading || !data ? (
            <CollectionSkeletonLoader count={12} />
          ) : (
            <Content>
              {!data.length ? (
                <EmptyState>
                  <h3>
                    {search
                      ? "No search results"
                      : "Your collections are empty"}
                  </h3>
                  <p>
                    {search
                      ? `No videos found for "${search}"`
                      : "You haven't created any collections yet."}
                  </p>
                  {!search && (
                    <Button onClick={() => navigate("/")}>Go Explore</Button>
                  )}
                </EmptyState>
              ) : (
                data.map((item, index) => {
                  return (
                    <CollectionItem
                      key={item._id}
                      video={item}
                      onOpenVideoModal={() =>
                        setIsGenerateCollectionModalOpen(true)
                      }
                      onCollectionItemClick={() =>
                        handleCollectionItemClick(item)
                      }
                      priority={index < 6}
                    />
                  );
                })
              )}
            </Content>
          )}
        </ContentContainer>
        <PaginationWrapper>
          <Pagination
            currentPage={page}
            totalCount={total}
            perPage={20}
            onPageChange={(page) => setPage(page)}
          />
        </PaginationWrapper>
      </Container>

      <GenerateCollectionModal
        isOpen={isGenerateCollectionModalOpen}
        onClose={() => setIsGenerateCollectionModalOpen(false)}
      />

      <CollectionModal
        isOpen={openCollectionModal}
        onClose={handleCollectionModalClose}
        video={selectedCollectionVideo}
        onOpenVideoModal={() => setIsGenerateCollectionModalOpen(true)}
        onDeleteClick={() => setShowDeleteConfirm(true)}
        onRateClick={() => setShowRateCollectionModal(true)}
      />
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Video"
        message="Are you sure you want to delete this video? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonType="danger"
        isLoading={deleteCollectionPending}
        theme="dark"
      />

      <RateCollectionModal
        isOpen={showRateCollectionModal}
        onClose={() => setShowRateCollectionModal(false)}
        onConfirm={handleRateCollection}
        rating={selectedCollectionVideo?.rating ?? 0}
      />
    </MainContainer>
  );
};

export default CollectionPage;
